import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { listMyInstructors } from "../integrations/supabase/instructor";
import { getWhatsNewAudience, useWhatsNew, type UseWhatsNewResult } from "./useWhatsNew";
import { useOwnerContext } from "./useOwnerContext";

/**
 * useWhatsNew bound to the signed-in account via the shared, cached owner context — safe to
 * call from the nav and the Getting Started page at once (no extra round trips; instances stay
 * in sync through the what's-new change event). Returns an empty, inert result for instructors,
 * client views and while the owner context is loading.
 *
 * Without `hasInstructor`, items gated on an instructor link stay hidden. That's fine for the
 * nav (its dots come from `hasNew`/`isPathNew`, which the always-shown items already drive);
 * Getting Started uses useGettingStartedWhatsNew to resolve the link.
 */
export const useCurrentUserWhatsNew = (
  options: { hasInstructor?: boolean } = {}
): UseWhatsNewResult => {
  const ownerContext = useOwnerContext().data;
  const resolved = useMemo(() => getWhatsNewAudience(ownerContext), [ownerContext]);
  const { hasInstructor } = options;
  const audience = useMemo(
    () => (resolved ? { ...resolved.audience, hasInstructor } : null),
    [resolved, hasInstructor]
  );
  return useWhatsNew({ userId: resolved?.userId, audience });
};

const MY_INSTRUCTORS_STALE_MS = 5 * 60 * 1000;

/**
 * Getting Started's what's-new: also resolves "does this account share with an instructor" from
 * the `["my-instructors", ownerId]` query Settings → Instructor Sharing already runs (same key,
 * one cache entry). Only fetched for tracking accounts; the instructor items stay hidden until
 * it resolves, or if it fails.
 */
export const useGettingStartedWhatsNew = (): UseWhatsNewResult => {
  const ownerContext = useOwnerContext().data;
  const ready = ownerContext?.status === "ready" ? ownerContext : null;
  const ownerId = ready?.couple.id ?? null;
  const canTrack =
    ready?.actorRole === "tracking" && !ready.viewingClientUserId && ready.actorUserId === ownerId;

  const instructorsQuery = useQuery({
    queryKey: ["my-instructors", ownerId] as const,
    queryFn: listMyInstructors,
    enabled: canTrack,
    staleTime: MY_INSTRUCTORS_STALE_MS,
    retry: false,
  });
  const hasInstructor = instructorsQuery.data ? instructorsQuery.data.length > 0 : undefined;

  return useCurrentUserWhatsNew({ hasInstructor });
};
